/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Link } from '@/components/link';
import type { NavCategory } from '@/lib/navigation/types';
import { toCatalogStaticUrl } from '@/lib/images/dynamic-image';

/**
 * A leaf entry of a subnav column, carrying the SCO-NAV-05 variations it was configured
 * with: an icon to its left (D) and an italic subline beneath (B). Deliberately not wrapped
 * in Radix's `NavigationMenuLink` so the same component serves the mobile drawer, which has
 * no navigation-menu context.
 */
export function SubnavLink({ category, onNavigate }: { category: NavCategory; onNavigate?: () => void }): ReactElement {
    const { icon, informationalText } = category;
    const config = useConfig();
    const iconSrc = toCatalogStaticUrl({ src: icon, config }) ?? icon;

    return (
        <Link to={category.url} onClick={onNavigate} className="group inline-flex items-center gap-2">
            {iconSrc ? (
                // Static media from `c_navIcon`, not a product image — DIS has nothing to
                // resize on an SVG. Dimensions are set so the row cannot shift as it loads.
                <img src={iconSrc} alt="" width={28} height={28} className="size-7 shrink-0 object-cover" />
            ) : null}

            <span className="flex flex-col items-start gap-1">
                <span className="text-body-s border-b border-transparent text-primary group-hover:border-muted-foreground group-focus-visible:border-muted-foreground">
                    {category.name}
                </span>
                {informationalText ? (
                    <span className="text-caption-m text-primary italic">{informationalText}</span>
                ) : null}
            </span>
        </Link>
    );
}
